import shortid from "shortid";

import { Stem, StemTextGapMatch } from "@examspark/types";

import { getNewScore } from "./utils";

// this item type's data structure is missing the "drops" object
// parent that we use to discriminate between the different response types,
// so we have to define it here
type OldTextGapMatchResponse = {
  isNewReponseShape: never;
  [gapId: string]: any;
};
export type NewTextGapMatchResponse = {
  isNewReponseShape: true;
  drops: Array<Stem.Drop>;
};
export type TextGapMatchResponse =
  | OldTextGapMatchResponse
  | NewTextGapMatchResponse;

const handleOldResponseShape = (response: TextGapMatchResponse | undefined) => {
  // map between old and new response shapes
  return response?.isNewReponseShape
    ? (response as NewTextGapMatchResponse)
    : ({
        isNewReponseShape: true,
        drops: Object.entries(
          (response as unknown as OldTextGapMatchResponse) ?? {},
        ).map(
          ([k, v]) =>
            ({
              draggableId: v.id,
              droppableId: k,
              instanceId: shortid.generate(),
            }) as Stem.Drop,
        ),
      } as NewTextGapMatchResponse);
};

export const scoreTextGapMatch = (
  expected: StemTextGapMatch,
  actual: Stem.Response,
) => {
  const score = getNewScore();
  const { scoring } = expected;

  // type shenanigans because the old response shape for this interaction type
  // was missing the "drops" object parent that we use to discriminate between
  // the different response types
  const response = handleOldResponseShape(
    actual as unknown as TextGapMatchResponse,
  );

  const pairs = scoring?.pairs;
  if (!pairs) return score;

  // gaps with at least one correct answer
  const scorables = Object.entries(expected.content.gaps ?? {}).filter(
    ([gapId]) => pairs.some((pair) => pair.dst === gapId),
  );

  score.max = scorables.length;

  const scoredGaps = scorables.map(([gapId, gap]) => {
    const options = expected.content.options;

    const dropsInGap = response.drops
      .filter((drop) => drop.droppableId === gapId)
      .map((drop) => ({
        drop: drop,
        option: options.find((option) => option.id === drop.draggableId),
      }))
      .filter((drop): drop is DropValue => !!drop.option);
    const pairsForGap = pairs.filter((pair) => pair.dst === gapId);

    const score = scoreGap(
      scoring.gapMatchScoringMethod,
      dropsInGap,
      pairsForGap,
      gap.allowMultipleTokens,
    );

    return score;
  });

  score.actual = scoredGaps.filter(Boolean).length;

  return score;
};

type DropValue = {
  drop: Stem.Drop;
  option: Stem.Option;
};

export const scoreGap = (
  scoringType: Stem.Scoring["gapMatchScoringMethod"] | undefined,
  dropsInGap: DropValue[],
  pairsForGap: Array<Stem.Pair>,
  gapAcceptsMultipleTokens: boolean | undefined,
) => {
  if (!scoringType || scoringType === "TOKEN_PLACEMENT") {
    return scorePlacementGap(
      dropsInGap,
      pairsForGap,
      !!gapAcceptsMultipleTokens,
    );
  } else if (scoringType === "GAP_VALUE") {
    return scoreValueGap(dropsInGap, pairsForGap);
  }
};

const scoreValueGap = (
  dropsInGap: DropValue[],
  pairsForGap: Array<Stem.Pair>,
) => {
  return pairsForGap.every((pair) => {
    const dropSum = dropsInGap.reduce((acc, drop) => {
      return acc + (drop.option.numericValue ?? 0);
    }, 0);

    return pair.gapValue === dropSum;
  });
};

const scorePlacementGap = (
  dropsInGap: DropValue[],
  pairsForGap: Array<Stem.Pair>,
  gapAcceptsMultipleTokens: boolean,
) => {
  const pairsLeftToScore = [...pairsForGap].filter((pair) => pair?.isKey);

  // if the gap accepts multiple tokens, then we assume that ALL relevant pairs must
  // be present
  if (gapAcceptsMultipleTokens) {
    return (
      dropsInGap.every((drop) => {
        const pair = pairsLeftToScore.find(
          (p) => p.src === drop.drop.draggableId,
        );

        if (pair) {
          pairsLeftToScore.splice(pairsLeftToScore.indexOf(pair), 1);
        }

        return pair;
      }) && pairsLeftToScore.length === 0
    );
    // if the gap only accepts one token, then we assume that only ONE relevant pair
    // must be present
  } else {
    return pairsForGap.some((pair) => {
      const drop = dropsInGap.find(
        (drop) => drop.drop.draggableId === pair.src,
      );

      return drop;
    });
  }
};
